import { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import ReactQuill from "react-quill-new";
import "react-quill-new/dist/quill.snow.css";
import {
  FileText, Plus, Edit, Trash2, Save, Loader2, Eye, EyeOff,
  X, Calendar, Tag, Image as ImageIcon, Sparkles, Upload
} from "lucide-react";

const API = "/api";

const CATEGORIES = ["Guida", "Risparmio", "Tecnologia", "Incentivi", "Consigli"];

const STATUS_BADGE = {
  published: { label: "Pubblicato", color: "bg-emerald-100 text-emerald-700" },
  draft: { label: "Bozza", color: "bg-slate-100 text-slate-600" },
};

const uploadImage = async (file) => {
  const formData = new FormData();
  formData.append("file", file);
  const res = await axios.post(`${API}/upload/image`, formData, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  return res.data.url;
};

const ArticleEditorModal = ({ article, onClose, onSave }) => {
  const [title, setTitle] = useState(article?.title || "");
  const [content, setContent] = useState(article?.content || "");
  const [excerpt, setExcerpt] = useState(article?.excerpt || "");
  const [category, setCategory] = useState(article?.category || "Guida");
  const [coverImage, setCoverImage] = useState(article?.cover_image || "");
  const [status, setStatus] = useState(article?.status || "draft");
  const [saving, setSaving] = useState(false);
  const [uploadingCover, setUploadingCover] = useState(false);
  const [quillRef, setQuillRef] = useState(null);

  const handleCoverUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingCover(true);
    try {
      const url = await uploadImage(file);
      setCoverImage(url);
      toast.success("Immagine caricata");
    } catch {
      toast.error("Errore nel caricamento immagine");
    } finally {
      setUploadingCover(false);
    }
  };

  const quillImageHandler = useCallback(() => {
    const input = document.createElement("input");
    input.setAttribute("type", "file");
    input.setAttribute("accept", "image/jpeg,image/png,image/webp,image/gif");
    input.click();
    input.onchange = async () => {
      const file = input.files?.[0];
      if (!file) return;
      try {
        const url = await uploadImage(file);
        const editor = quillRef?.getEditor();
        if (editor) {
          const range = editor.getSelection(true);
          editor.insertEmbed(range.index, "image", url);
          editor.setSelection(range.index + 1);
        }
        toast.success("Immagine inserita");
      } catch {
        toast.error("Errore nel caricamento immagine");
      }
    };
  }, [quillRef]);

  const quillModules = useMemo(() => ({
    toolbar: {
      container: [
        [{ header: [2, 3, false] }],
        ["bold", "italic", "underline", "strike"],
        [{ list: "ordered" }, { list: "bullet" }],
        ["blockquote"],
        ["link", "image"],
        ["clean"],
      ],
      handlers: {
        image: quillImageHandler,
      },
    },
  }), [quillImageHandler]);

  const handleSave = async () => {
    if (!title.trim()) {
      toast.error("Il titolo e obbligatorio");
      return;
    }
    setSaving(true);
    try {
      const data = { title, content, excerpt, category, cover_image: coverImage, status };
      if (article?.id) {
        await axios.put(`${API}/admin/articles/${article.id}`, data);
        toast.success("Articolo aggiornato");
      } else {
        await axios.post(`${API}/admin/articles`, data);
        toast.success("Articolo creato");
      }
      onSave();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore nel salvataggio");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center p-4 bg-slate-900/50 backdrop-blur-sm overflow-y-auto" data-testid="article-editor-modal">
      <div className="bg-white rounded-2xl max-w-4xl w-full shadow-2xl animate-fade-in-up my-8">
        {/* Header */}
        <div className="px-6 py-5 border-b border-slate-100 flex items-center justify-between sticky top-0 bg-white rounded-t-2xl z-10">
          <h3 className="text-lg font-bold text-slate-900 flex items-center gap-2" style={{ fontFamily: "Outfit, sans-serif" }} data-testid="editor-title">
            <FileText className="w-5 h-5 text-emerald-600" />
            {article?.id ? "Modifica Articolo" : "Nuovo Articolo"}
          </h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600 w-8 h-8 flex items-center justify-center rounded-full hover:bg-slate-100" data-testid="editor-close">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="px-6 py-6 space-y-5">
          {/* Title */}
          <div>
            <label className="text-sm font-semibold text-slate-700 mb-1.5 block">Titolo</label>
            <Input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Titolo dell'articolo..."
              className="h-12 rounded-xl text-lg font-medium input-focus-ring"
              data-testid="editor-title-input"
            />
          </div>

          {/* Category + Status */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-semibold text-slate-700 mb-1.5 flex items-center gap-1.5">
                <Tag className="w-3.5 h-3.5" /> Categoria
              </label>
              <select
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                className="flex h-10 w-full rounded-xl border border-input bg-transparent px-3 py-1 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                data-testid="editor-category-select"
              >
                {CATEGORIES.map((c) => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-sm font-semibold text-slate-700 mb-1.5 block">Stato</label>
              <div className="flex gap-2">
                <button
                  onClick={() => setStatus("draft")}
                  className={`flex-1 h-10 rounded-xl text-sm font-medium flex items-center justify-center gap-1.5 transition-colors ${status === "draft" ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}
                  data-testid="editor-status-draft"
                >
                  <EyeOff className="w-3.5 h-3.5" /> Bozza
                </button>
                <button
                  onClick={() => setStatus("published")}
                  className={`flex-1 h-10 rounded-xl text-sm font-medium flex items-center justify-center gap-1.5 transition-colors ${status === "published" ? "bg-emerald-500 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}
                  data-testid="editor-status-published"
                >
                  <Eye className="w-3.5 h-3.5" /> Pubblicato
                </button>
              </div>
            </div>
          </div>

          {/* Cover Image */}
          <div>
            <label className="text-sm font-semibold text-slate-700 mb-1.5 flex items-center gap-1.5">
              <ImageIcon className="w-3.5 h-3.5" /> Immagine di copertina
            </label>
            <div className="flex gap-2">
              <Input
                value={coverImage}
                onChange={(e) => setCoverImage(e.target.value)}
                placeholder="URL immagine o carica un file..."
                className="h-10 rounded-xl input-focus-ring text-sm flex-1"
                data-testid="editor-cover-input"
              />
              <label
                className={`flex items-center gap-1.5 px-4 h-10 rounded-xl text-sm font-medium cursor-pointer transition-colors whitespace-nowrap ${uploadingCover ? "bg-slate-100 text-slate-400" : "bg-emerald-50 text-emerald-700 hover:bg-emerald-100"}`}
                data-testid="editor-cover-upload-btn"
              >
                {uploadingCover ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                {uploadingCover ? "Caricamento..." : "Carica"}
                <input
                  type="file"
                  accept="image/jpeg,image/png,image/webp,image/gif"
                  className="hidden"
                  onChange={handleCoverUpload}
                  disabled={uploadingCover}
                  data-testid="editor-cover-file-input"
                />
              </label>
            </div>
            {coverImage && (
              <div className="mt-2 rounded-xl overflow-hidden border border-slate-100 aspect-[3/1] relative group">
                <img src={coverImage} alt="Anteprima" className="w-full h-full object-cover" />
                <button
                  onClick={() => setCoverImage("")}
                  className="absolute top-2 right-2 bg-red-500 text-white w-7 h-7 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity shadow-lg"
                  data-testid="editor-cover-remove"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            )}
          </div>

          {/* Excerpt */}
          <div>
            <label className="text-sm font-semibold text-slate-700 mb-1.5 block">Estratto (anteprima)</label>
            <textarea
              value={excerpt}
              onChange={(e) => setExcerpt(e.target.value)}
              rows={2}
              className="flex w-full rounded-xl border border-input bg-transparent px-3 py-2 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring resize-none"
              placeholder="Breve descrizione dell'articolo..."
              data-testid="editor-excerpt-input"
            />
          </div>

          {/* Content (Rich Text) */}
          <div>
            <label className="text-sm font-semibold text-slate-700 mb-1.5 flex items-center gap-1.5">
              Contenuto <span className="text-xs text-slate-400 font-normal">(clicca l'icona immagine nella toolbar per caricare)</span>
            </label>
            <ReactQuill
              ref={(el) => setQuillRef(el)}
              value={content}
              onChange={setContent}
              modules={quillModules}
              placeholder="Scrivi il contenuto dell'articolo..."
              data-testid="editor-content"
            />
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-slate-100 flex items-center justify-end gap-3 sticky bottom-0 bg-white rounded-b-2xl">
          <button
            onClick={onClose}
            className="text-sm font-medium text-slate-500 hover:text-slate-700 px-4 py-2.5 rounded-xl hover:bg-slate-100 transition-colors"
            data-testid="editor-cancel"
          >
            Annulla
          </button>
          <button
            onClick={handleSave}
            disabled={saving || !title.trim()}
            className="bg-emerald-500 hover:bg-emerald-600 text-white font-bold rounded-full px-6 py-2.5 text-sm btn-scale flex items-center gap-2 disabled:opacity-50"
            data-testid="editor-save"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            {article?.id ? "Aggiorna" : "Crea Articolo"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default function AdminArticles() {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editArticle, setEditArticle] = useState(null);
  const [showEditor, setShowEditor] = useState(false);
  const [seeding, setSeeding] = useState(false);

  const fetchArticles = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/articles`);
      setArticles(res.data);
    } catch {
      toast.error("Errore nel caricamento articoli");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchArticles();
  }, [fetchArticles]);

  const handleDelete = async (id) => {
    if (!window.confirm("Sei sicuro di voler eliminare questo articolo?")) return;
    try {
      await axios.delete(`${API}/admin/articles/${id}`);
      toast.success("Articolo eliminato");
      fetchArticles();
    } catch {
      toast.error("Errore nell'eliminazione");
    }
  };

  const handleSeed = async () => {
    setSeeding(true);
    try {
      const res = await axios.post(`${API}/admin/articles/seed`);
      toast.success(res.data.message);
      fetchArticles();
    } catch {
      toast.error("Errore nel caricamento articoli demo");
    } finally {
      setSeeding(false);
    }
  };

  const openEditor = (article = null) => {
    setEditArticle(article);
    setShowEditor(true);
  };

  return (
    <div data-testid="admin-articles-section">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-6">
        <h2 className="text-base font-bold text-slate-900" style={{ fontFamily: "Outfit, sans-serif" }}>
          Articoli del Blog ({articles.length})
        </h2>
        <div className="flex items-center gap-2">
          {articles.length === 0 && (
            <button
              onClick={handleSeed}
              disabled={seeding}
              className="flex items-center gap-1.5 text-sm font-medium text-amber-600 hover:text-amber-700 bg-amber-50 hover:bg-amber-100 px-3 py-2 rounded-lg transition-colors disabled:opacity-50"
              data-testid="articles-seed-button"
            >
              {seeding ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
              Carica articoli demo
            </button>
          )}
          <button
            onClick={() => openEditor()}
            className="flex items-center gap-1.5 text-sm font-bold text-white bg-emerald-500 hover:bg-emerald-600 px-4 py-2 rounded-lg transition-colors btn-scale"
            data-testid="articles-new-button"
          >
            <Plus className="w-4 h-4" /> Nuovo Articolo
          </button>
        </div>
      </div>

      {/* Articles list */}
      <div className="bg-white rounded-xl border border-slate-100 overflow-hidden" data-testid="admin-articles-table">
        {loading ? (
          <div className="py-16 text-center">
            <Loader2 className="w-6 h-6 animate-spin text-slate-400 mx-auto" />
          </div>
        ) : articles.length === 0 ? (
          <div className="py-16 text-center" data-testid="admin-no-articles">
            <FileText className="w-12 h-12 text-slate-200 mx-auto mb-3" />
            <p className="text-sm text-slate-500 mb-1">Nessun articolo presente</p>
            <p className="text-xs text-slate-400">Crea il tuo primo articolo o carica gli articoli demo</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-slate-50 text-left">
                  <th className="px-5 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide">Titolo</th>
                  <th className="px-5 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide hidden sm:table-cell">Categoria</th>
                  <th className="px-5 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide">Stato</th>
                  <th className="px-5 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide hidden md:table-cell">Data</th>
                  <th className="px-5 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide w-24">Azioni</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-50">
                {articles.map((a) => {
                  const statusInfo = STATUS_BADGE[a.status] || STATUS_BADGE.draft;
                  return (
                    <tr key={a.id} className="hover:bg-slate-50/60 transition-colors" data-testid={`article-row-${a.id}`}>
                      <td className="px-5 py-3.5">
                        <div className="font-semibold text-slate-900 line-clamp-1">{a.title}</div>
                        <div className="text-xs text-slate-400 mt-0.5">/{a.slug}</div>
                      </td>
                      <td className="px-5 py-3.5 hidden sm:table-cell">
                        <span className="text-xs font-medium text-slate-600">{a.category || "—"}</span>
                      </td>
                      <td className="px-5 py-3.5">
                        <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-semibold ${statusInfo.color}`}>
                          {a.status === "published" ? <Eye className="w-3 h-3" /> : <EyeOff className="w-3 h-3" />}
                          {statusInfo.label}
                        </span>
                      </td>
                      <td className="px-5 py-3.5 hidden md:table-cell text-xs text-slate-400">
                        {new Date(a.created_at).toLocaleDateString("it-IT")}
                      </td>
                      <td className="px-5 py-3.5">
                        <div className="flex items-center gap-1">
                          <button
                            onClick={() => openEditor(a)}
                            className="p-2 rounded-lg hover:bg-slate-100 text-slate-400 hover:text-emerald-600 transition-colors"
                            data-testid={`article-edit-${a.id}`}
                          >
                            <Edit className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleDelete(a.id)}
                            className="p-2 rounded-lg hover:bg-red-50 text-slate-400 hover:text-red-600 transition-colors"
                            data-testid={`article-delete-${a.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Editor Modal */}
      {showEditor && (
        <ArticleEditorModal
          article={editArticle}
          onClose={() => { setShowEditor(false); setEditArticle(null); }}
          onSave={fetchArticles}
        />
      )}
    </div>
  );
}
